import { FileUpIcon } from 'lucide-react';
import { type JSX, useRef, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  ArtifactRevisionConflictError,
  useSaveArtifactRevision,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';
import { notify } from '@/lib/notify.js';

import { artifactUploadRejection } from './artifact-editing.js';
import { formatBytes } from './artifact-display.js';

const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

/** The suffix is the reliable type signal when browsers leave `File.type` empty or report a generic MIME. */
function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return null;
  return name.slice(dot).toLowerCase();
}

function mimeEssence(contentType: string): string {
  return contentType.toLowerCase().split(';', 1)[0]!.trim();
}

function isSpecificMime(contentType: string): boolean {
  const type = mimeEssence(contentType);
  return type.length > 0 && type !== FALLBACK_CONTENT_TYPE;
}

/**
 * Reject a local file that cannot safely become the next revision. The artifact keeps its name, so a
 * different format would make its contents lie about their name. A matching extension is enough because
 * browsers commonly report markdown as empty and CSV as `application/vnd.ms-excel`; an exact, specific MIME
 * can also confirm a renamed replacement. Extensionless artifacts require a verifiable matching MIME.
 */
export function artifactRevisionUploadRejection(
  artifact: Pick<WorkspaceArtifact, 'contentType' | 'name'>,
  file: Pick<File, 'name' | 'size' | 'type'>,
): string | null {
  const sizeRejection = artifactUploadRejection(file);
  if (sizeRejection !== null) return sizeRejection;
  if (file.size === 0) return `“${file.name}” is empty.`;

  const expectedExtension = extensionOf(artifact.name);
  const pickedExtension = extensionOf(file.name);
  if (expectedExtension !== null && pickedExtension !== expectedExtension) {
    const mimeConfirmsType =
      isSpecificMime(artifact.contentType) &&
      isSpecificMime(file.type) &&
      mimeEssence(artifact.contentType) === mimeEssence(file.type);
    if (!mimeConfirmsType)
      return `Choose a ${expectedExtension} file so it matches “${artifact.name}”.`;
  }

  if (expectedExtension === null && isSpecificMime(artifact.contentType)) {
    if (
      !isSpecificMime(file.type) ||
      mimeEssence(artifact.contentType) !== mimeEssence(file.type)
    )
      return `Choose a ${mimeEssence(artifact.contentType)} file so it matches “${artifact.name}”.`;
  }
  return null;
}

/** The native picker hint. Validation above remains authoritative because `accept` can be bypassed. */
function acceptedRevisionTypes(
  artifact: WorkspaceArtifact,
): string | undefined {
  const extension = extensionOf(artifact.name);
  const contentType = mimeEssence(artifact.contentType);
  const values = [
    extension,
    isSpecificMime(contentType) ? contentType : null,
  ].filter((value): value is string => value !== null);
  return values.length > 0 ? values.join(',') : undefined;
}

/**
 * Upload arbitrary bytes as the next revision of one existing artifact. Its identity, name, access and
 * history stay in place; only the head bytes move forward. The optimistic base revision prevents a stale
 * dialog from overwriting a version somebody else uploaded first.
 */
export function ArtifactUploadRevisionDialog({
  workspaceId,
  artifact,
  onSaved,
  onClose,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  onSaved?: (updated: WorkspaceArtifact) => void;
  onClose: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const save = useSaveArtifactRevision(workspaceId);
  const [picked, setPicked] = useState<File | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  function selectFile(file: File): void {
    const rejection = artifactRevisionUploadRejection(artifact, file);
    setPicked(rejection === null ? file : null);
    setFailure(rejection);
  }

  function upload(): void {
    if (picked === null) {
      setFailure('Choose a file to upload.');
      return;
    }
    const rejection = artifactRevisionUploadRejection(artifact, picked);
    if (rejection !== null) {
      setFailure(rejection);
      return;
    }

    setFailure(null);
    // Keep the artifact's canonical MIME when it has one. Browsers can report compatible files with
    // aliases (notably CSV as application/vnd.ms-excel), and that local alias must not rewrite the
    // content type used to sign and store the next revision.
    const contentType = isSpecificMime(artifact.contentType)
      ? artifact.contentType
      : picked.type || artifact.contentType || FALLBACK_CONTENT_TYPE;
    save.mutate(
      {
        id: artifact.id,
        body: picked,
        contentType,
        baseRevision: artifact.headRevision,
      },
      {
        onSuccess: (updated) => {
          notify.success(`Version ${updated.headRevision} was uploaded`);
          onSaved?.(updated);
          onClose();
        },
        onError: (error) => {
          setFailure(
            error instanceof ArtifactRevisionConflictError
              ? 'Someone else uploaded a new version first. Close this dialog, review the latest version, and try again.'
              : error.message,
          );
        },
      },
    );
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Upload new version"
      description={`Replace the current contents of “${artifact.name}”. Its name, access and previous versions will stay unchanged.`}
      className="sm:max-w-md"
    >
      <div className="mt-3 flex flex-col gap-3">
        <button
          type="button"
          className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-5 text-center transition-colors hover:bg-muted/40"
          onClick={() => inputRef.current?.click()}
          disabled={save.isPending}
        >
          <FileUpIcon className="size-6 text-muted-foreground" />
          <span className="text-sm font-medium">
            {picked?.name ?? 'Choose a replacement file'}
          </span>
          <span className="text-xs text-muted-foreground">
            {picked
              ? formatBytes(picked.size)
              : 'The file must use the same format and be no larger than 25 MB.'}
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          aria-label={`Choose a new version for ${artifact.name}`}
          accept={acceptedRevisionTypes(artifact)}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) selectFile(file);
          }}
        />
        {failure !== null && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {`This will create version ${artifact.headRevision + 1}. Version ${artifact.headRevision} will remain in history.`}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={upload}
            disabled={picked === null || save.isPending}
          >
            <FileUpIcon className="size-4" />
            {save.isPending
              ? 'Uploading…'
              : `Upload as version ${artifact.headRevision + 1}`}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
